import { useReveal } from "@/hooks/useReveal";

const CASES = [
  {
    code: "C.01",
    image:
      "https://images.unsplash.com/photo-1590496793907-4d66e2994b4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxzaGlwcGluZyUyMHBvcnQlMjBuaWdodCUyMGNhcmdvfGVufDB8fHx8MTc3OTE0MTE0N3ww&ixlib=rb-4.1.0&q=85",
    title: "Reduced Empty Miles in a National Grocery Network",
    focus:
      "Multi-DC transportation, backhaul routing, load planning, continuous movement, and savings logic.",
    saw: "Empty repositioning miles were absorbing margin across two regions while loads were available on adjacent lanes.",
    missed:
      "Reports framed it as a transportation cost issue rather than a load-pairing and continuous-movement design issue.",
    changed:
      "Lane symmetry, backhaul logic, and continuous-movement rules were redesigned at the planning layer — not the dispatch layer.",
  },
  {
    code: "C.02",
    image:
      "https://images.unsplash.com/photo-1773966071353-0956f870e034?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBpbnRlcmlvciUyMGRhcmslMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc3OTE0MTE0N3ww&ixlib=rb-4.1.0&q=85",
    title: "Perishable Forecasting Across 40+ Distribution Centers",
    focus:
      "Forecasting discipline, inventory planning, shrink reduction, DC-level complexity, regional variability.",
    saw: "Forecast bias varied dramatically by region — and that variance was traveling into shrink and stock-outs simultaneously.",
    missed:
      "Centralized forecast accuracy KPIs masked the DC-level distortion driving the actual financial damage.",
    changed:
      "Forecast accountability was localized at the DC tier with regional planning discipline and shrink-aware sensitivity bands.",
  },
  {
    code: "C.03",
    image:
      "https://images.unsplash.com/photo-1590497008432-598f04441de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMHBvcnQlMjBuaWdodCUyMGNhcmdvfGVufDB8fHx8MTc3OTE0MTE0N3ww&ixlib=rb-4.1.0&q=85",
    title: "EDI Visibility as the Supply Chain Nervous System",
    focus:
      "EDI 214 events, timestamps, exception detection, dwell visibility, carrier/customer/vendor alignment.",
    saw: "EDI events were arriving but were not being read as operational signals — only as compliance documents.",
    missed:
      "Dwell, exception, and appointment failures were already visible in the EDI feed days before they hit the dashboard.",
    changed:
      "EDI was reframed as the operational telemetry layer, surfacing dwell and exception in near-real-time.",
  },
];

export default function CaseStudies() {
  const { ref, visible } = useReveal();

  return (
    <section
      id="case-studies"
      ref={ref}
      data-testid="case-studies-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 07 / CASE STUDIES</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="case-studies-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              Proof Without <br />
              <span className="text-axiom-muted">the Noise.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className={`text-axiom-muted text-base sm:text-lg leading-relaxed reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Axiom-AI's perspective is built from national-scale operating
              experience across transportation, grocery distribution,
              forecasting, network design, private fleet strategy, and
              continuous improvement.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CASES.map((c, i) => (
            <article
              key={c.code}
              data-testid={`case-card-${c.code}`}
              className={`bg-axiom-bg flex flex-col reveal ${visible ? "in" : ""}`}
              style={{ transitionDelay: `${100 + i * 100}ms` }}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover opacity-50 hover:opacity-70 transition-opacity duration-500 grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-axiom-bg/20 via-axiom-bg/40 to-axiom-bg" />
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                  <span className="num-label bg-axiom-bg/80 px-2 py-1 border border-white/10">
                    {c.code}
                  </span>
                  <span className="signal-dot" style={{ width: 6, height: 6 }} />
                </div>
              </div>

              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <h3 className="font-display text-xl md:text-2xl text-axiom-text font-bold leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 text-xs uppercase tracking-wider text-axiom-dim">
                  Focus
                </p>
                <p className="mt-1 text-sm text-axiom-muted leading-relaxed">
                  {c.focus}
                </p>

                <div className="mt-6 space-y-4 border-t border-white/10 pt-5">
                  <div>
                    <div className="num-label text-axiom-accent">WHAT AXIOM SAW</div>
                    <p className="mt-1 text-sm text-axiom-text/90 leading-relaxed">
                      {c.saw}
                    </p>
                  </div>
                  <div>
                    <div className="num-label">WHAT OTHERS MISSED</div>
                    <p className="mt-1 text-sm text-axiom-muted leading-relaxed">
                      {c.missed}
                    </p>
                  </div>
                  <div>
                    <div className="num-label text-axiom-accent">WHAT CHANGED</div>
                    <p className="mt-1 text-sm text-axiom-text/90 leading-relaxed">
                      {c.changed}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
